// Campaign creation wizard with recipient input method selection
// Version: 2.0 - Updated January 9, 2026 with CSV/Manual selection
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PollIcon from '@mui/icons-material/Poll';
import EmailIcon from '@mui/icons-material/Email';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import GroupsIcon from '@mui/icons-material/Groups';
import BadgeIcon from '@mui/icons-material/Badge';
import PeopleIcon from '@mui/icons-material/People';
import TerminalIcon from '@mui/icons-material/Terminal';
import { apiClient, uploadCampaign, getCampaignDetails, updateCampaign, fetchPopulation, resolveDistributionList, PopulationType, PopulationRecipient } from '../services/ecm-api';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SurveyBuilder, { Survey } from './SurveyBuilder';

interface SendGridTemplate {
  id: string;
  name: string;
  updated_at: string;
  subject?: string;
}

interface FromAddress {
  name: string;
  email: string;
}

// Message shown when Fetch Recipients fails. For student populations the backend answers
// 502 {error, failedDatabases} instead of a partial list; that message is written for staff
// and is shown as-is. Any other error body (setup or technical text) gets a plain message.
function populationFetchErrorMessage(err: any): string {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 502 && Array.isArray(data?.failedDatabases) && typeof data?.error === 'string' && data.error) {
    return data.error;
  }
  if (status === 401) {
    return 'Your session has expired, so no recipient list was produced. Sign in again, then fetch the recipients.';
  }
  return 'Could not fetch recipients, so no recipient list was produced. Please try again in a few minutes, and contact IT if this keeps happening.';
}

// Message shown under the template pickers when get-templates refuses the caller. The route needs a
// signed-in SIMS Admin or Campaign Manager (the roles that can create a campaign). '' = not a refusal.
function templatesAuthMessage(err: any): string {
  const status = err?.response?.status;
  if (status === 401) {
    return 'Your session has expired, so the SendGrid templates could not be loaded. Sign in again, then reopen this page.';
  }
  if (status === 403) {
    return 'Your account cannot load SendGrid templates. Only SIMS Admins and Campaign Managers can create campaigns; ask IT if you need that access.';
  }
  return '';
}

export default function NewCampaign({ editId: editIdProp }: { editId?: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editIdFromUrl = searchParams.get('edit');
  const editId = editIdProp || editIdFromUrl;
  const isEditMode = !!editId;
  const [loadingCampaign, setLoadingCampaign] = useState(isEditMode);
  type CampaignType = 'email' | 'survey' | 'canvas_announcement' | 'modo_push';
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState<CampaignType[]>([]);
  const toggleType = (type: CampaignType) =>
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  // Canvas Announcement specific state
  const [canvasTitle, setCanvasTitle] = useState('');
  const [canvasBody, setCanvasBody] = useState('');
  const [canvasNotifyStudents, setCanvasNotifyStudents] = useState(true);
  const [canvasDelayPosting, setCanvasDelayPosting] = useState(false);
  const [canvasDelayPostAt, setCanvasDelayPostAt] = useState('');
  const [canvasAlsoEmail, setCanvasAlsoEmail] = useState(false);
  // Modo Push specific state
  const [modoTitle, setModoTitle] = useState('');
  const [modoMessage, setModoMessage] = useState('');
  const [modoNotifyPush, setModoNotifyPush] = useState(true);
  const [modoAlsoEmail, setModoAlsoEmail] = useState(false);
  const [modoDeepLinkUrl, setModoDeepLinkUrl] = useState('');
  const [modoDelay, setModoDelay] = useState(false);
  const [modoScheduleAt, setModoScheduleAt] = useState('');
  type ModoAudience = 'all' | 'employees' | 'students';
  const EMPLOYEE_POPULATIONS = ['faculty', 'it_systems_devops'];
  const STUDENT_POPULATIONS  = ['current_students', 'withdrawn_students', 'alumni'];
  const deriveModoAudience = (pops: string[]): ModoAudience => {
    const hasEmployee = pops.some(p => EMPLOYEE_POPULATIONS.includes(p));
    const hasStudent  = pops.some(p => STUDENT_POPULATIONS.includes(p));
    if (hasEmployee && hasStudent) return 'all';
    if (hasEmployee) return 'employees';
    if (hasStudent)  return 'students';
    return 'all';
  };
  // modoAudience kept for draft restore compatibility but derived at submit time
  const [modoAudience, setModoAudience] = useState<ModoAudience>('all');
  type ModoEnvironment = 'test' | 'production';
  const [modoEnvironment, setModoEnvironment] = useState<ModoEnvironment>('test');
  const [selectedPopulations, setSelectedPopulations] = useState<PopulationType[]>([]);
  // Bumped whenever the population selection changes or a fetch starts, so a slow response
  // for an earlier selection can never land as the current recipient list.
  const populationRequestId = useRef(0);
  const togglePopulation = (p: PopulationType) => {
    // The other half of the mutual exclusion with "I'll provide the recipient list
    // myself": touching a group means the campaign is population-driven again.
    setOwnRecipientList(false);
    setSelectedPopulations(prev => prev.includes(p) ? prev.filter(t => t !== p) : [...prev, p]);
    // A list fetched for the previous selection no longer matches it: require a new fetch.
    populationRequestId.current += 1;
    setPopulationRecipients([]);
    setPopulationFetched(false);
    setPopulationLoading(false);
    setPopulationError('');
  };
  const [populationRecipients, setPopulationRecipients] = useState<PopulationRecipient[]>([]);
  const [populationLoading, setPopulationLoading] = useState(false);
  const [populationFetched, setPopulationFetched] = useState(false);
  // Shown next to Fetch Recipients. The server returns 502 with a plain-language `error`
  // when a campus database fails, instead of a partial list; that message is shown as-is.
  const [populationError, setPopulationError] = useState('');
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{emails: string[], checked: boolean}>({emails: [], checked: false});

  // Distribution List (email campaigns only)
  const [dlEmail, setDlEmail] = useState('');
  const [dlRecipients, setDlRecipients] = useState<PopulationRecipient[]>([]);
  const [dlLoading, setDlLoading] = useState(false);
  const [dlResolved, setDlResolved] = useState(false);
  const [dlGroupName, setDlGroupName] = useState('');
  // Step 1 offers Target Population and Distribution List, and Continue required one of
  // them — so a campaign whose recipients come from neither had no way past this screen,
  // even though step 3 has always accepted a CSV. This is the explicit third answer.
  const [ownRecipientList, setOwnRecipientList] = useState(false);

  const [recipientInputMethod, setRecipientInputMethod] = useState<'csv' | 'manual' | 'population'>('csv');
  const [file, setFile] = useState<File | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [surveyTemplateId, setSurveyTemplateId] = useState(''); // Template for survey invitation email
  const [survey, setSurvey] = useState<Survey | null>(null);
  // Email subject is automatically fetched from template
  // const [emailSubject, setEmailSubject] = useState('');
  const [templates, setTemplates] = useState<SendGridTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  // Set when get-templates refuses the caller (401 / 403), shown in place of "No templates found".
  const [templatesAuthError, setTemplatesAuthError] = useState('');

  // From Address Management - Load from localStorage or use default
  const defaultAddress = { name: 'Laurus College Academics', email: 'academics@lauruscollege.edu' };
  const [fromAddresses, setFromAddresses] = useState<FromAddress[]>(() => {
    try {
      const saved = localStorage.getItem('ecm_from_addresses');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Always ensure default address is included
        if (!parsed.some((addr: FromAddress) => addr.email === defaultAddress.email)) {
          return [defaultAddress, ...parsed];
        }
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load saved from addresses:', error);
    }
    return [defaultAddress];
  });
  const [fromEmail, setFromEmail] = useState(() => fromAddresses[0].email);
  const [fromName, setFromName] = useState(() => fromAddresses[0].name);

  // Address book dialog
  const [openAddressBook, setOpenAddressBook] = useState(false);
  const [newFromName, setNewFromName] = useState('');
  const [newFromEmail, setNewFromEmail] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  // Load campaign data for editing
  useEffect(() => {
    const loadCampaignForEdit = async () => {
      if (!editId) return;
      
      try {
        setLoadingCampaign(true);
        console.log('📝 Loading campaign for edit:', editId);
        const campaign = await getCampaignDetails(editId);
        
        // Populate form with campaign data
        setCampaignName(campaign.title);
        setTemplateId(campaign.email_template);
        setSurveyTemplateId(campaign.email_template); // For survey campaigns
        setFromEmail(campaign.from_email);
        setFromName(campaign.from_name);
        
        // Determine campaign type from campaign_type field or fallback to legacy detection
        if (campaign.campaign_type) {
          setSelectedTypes([campaign.campaign_type as CampaignType]);
        } else if (campaign.survey_id && campaign.survey_id !== 'email-only') {
          setSelectedTypes(['survey']);
        } else if (campaign.communication_channels?.includes('canvas_announcement')) {
          setSelectedTypes(['canvas_announcement']);
        } else {
          setSelectedTypes(['email']);
        }
        
        // Load survey from localStorage if it's a survey campaign
        const isSurvey = campaign.campaign_type === 'survey' ||
          (!campaign.campaign_type && campaign.survey_id && campaign.survey_id !== 'email-only');
        if (isSurvey) {
          const surveyKey = `survey_${campaign.title}`;
          const surveyData = localStorage.getItem(surveyKey);
          if (surveyData) {
            setSurvey(JSON.parse(surveyData));
          }
        }
        
        console.log('✅ Campaign loaded for editing');
      } catch (error) {
        console.error('❌ Failed to load campaign:', error);
        setError('Failed to load campaign for editing');
      } finally {
        setLoadingCampaign(false);
      }
    };
    
    loadCampaignForEdit();
  }, [editId]);

  // Load draft from localStorage on mount (only if not in edit mode)
  useEffect(() => {
    if (isEditMode) return; // Skip draft loading in edit mode
    
    const loadDraft = () => {
      try {
        // Purge stale v1 draft (may contain removed population types like 'staff')
        localStorage.removeItem('ecm_campaign_draft');
        const savedDraft = localStorage.getItem('ecm_campaign_draft_v2');
        if (savedDraft) {
          const draft = JSON.parse(savedDraft);
          console.log('📋 Loading campaign draft from localStorage');
          setActiveStep(draft.activeStep || 0);
          setSelectedTypes(draft.selectedTypes || []);
          setRecipientInputMethod(draft.recipientInputMethod || 'csv');
          setCampaignName(draft.campaignName || '');
          setTemplateId(draft.templateId || '');
          setSurveyTemplateId(draft.surveyTemplateId || '');
          if (draft.survey) setSurvey(draft.survey);
          setFromEmail(draft.fromEmail || fromAddresses[0].email);
          setFromName(draft.fromName || fromAddresses[0].name);
          if (draft.canvasTitle) setCanvasTitle(draft.canvasTitle);
          if (draft.canvasBody) setCanvasBody(draft.canvasBody);
          if (draft.canvasNotifyStudents !== undefined) setCanvasNotifyStudents(draft.canvasNotifyStudents);
          if (draft.canvasDelayPosting !== undefined) setCanvasDelayPosting(draft.canvasDelayPosting);
          if (draft.canvasDelayPostAt) setCanvasDelayPostAt(draft.canvasDelayPostAt);
          if (draft.canvasAlsoEmail !== undefined) setCanvasAlsoEmail(draft.canvasAlsoEmail);
          if (draft.modoTitle) setModoTitle(draft.modoTitle);
          if (draft.modoMessage) setModoMessage(draft.modoMessage);
          if (draft.modoNotifyPush !== undefined) setModoNotifyPush(draft.modoNotifyPush);
          if (draft.modoAlsoEmail !== undefined) setModoAlsoEmail(draft.modoAlsoEmail);
          if (draft.modoDeepLinkUrl) setModoDeepLinkUrl(draft.modoDeepLinkUrl);
          if (draft.modoDelay !== undefined) setModoDelay(draft.modoDelay);
          if (draft.modoScheduleAt) setModoScheduleAt(draft.modoScheduleAt);
          if (draft.modoAudience) setModoAudience(draft.modoAudience);
          if (draft.modoEnvironment) setModoEnvironment(draft.modoEnvironment);
          if (draft.selectedPopulations) {
            const validPops: PopulationType[] = ['current_students', 'withdrawn_students', 'alumni', 'faculty', 'it_systems_devops'];
            setSelectedPopulations(draft.selectedPopulations.filter((p: string) => validPops.includes(p as PopulationType)));
          }
          if (draft.dlEmail) setDlEmail(draft.dlEmail);
          if (draft.ownRecipientList) setOwnRecipientList(true);
        }
      } catch (error) {
        console.error('Failed to load campaign draft:', error);
      }
    };
    loadDraft();
  }, [isEditMode]);

  // Save draft to localStorage whenever state changes
  useEffect(() => {
    const saveDraft = () => {
      try {
        const draft = {
          activeStep,
          selectedTypes,
          selectedPopulations,
          recipientInputMethod,
          campaignName,
          templateId,
          surveyTemplateId,
          survey,
          fromEmail,
          fromName,
          canvasTitle,
          canvasBody,
          canvasNotifyStudents,
          canvasDelayPosting,
          canvasDelayPostAt,
          canvasAlsoEmail,
          modoTitle,
          modoMessage,
          modoNotifyPush,
          modoAlsoEmail,
          modoDeepLinkUrl,
          modoDelay,
          modoScheduleAt,
          modoAudience,
          modoEnvironment,
          dlEmail,
          ownRecipientList,
          savedAt: new Date().toISOString()
        };
        localStorage.setItem('ecm_campaign_draft_v2', JSON.stringify(draft));
      } catch (error) {
        console.error('Failed to save campaign draft:', error);
      }
    };
    // Only save if there's actual content
    if (campaignName || templateId || surveyTemplateId || survey || file) {
      saveDraft();
    }
  }, [activeStep, selectedTypes, selectedPopulations, recipientInputMethod, ownRecipientList, campaignName, templateId, surveyTemplateId, survey, fromEmail, fromName, canvasTitle, canvasBody, canvasNotifyStudents, canvasDelayPosting, canvasDelayPostAt, canvasAlsoEmail, modoTitle, modoMessage, modoNotifyPush, modoAlsoEmail, modoDeepLinkUrl, modoDelay, modoScheduleAt, modoAudience, modoEnvironment, file]);

  // Fetch templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        console.log('🔄 Fetching templates from: /get-templates');
        // get-templates needs a signed-in Survey.Admin or Campaign.Manager. apiClient asks MSAL for
        // a fresh token on every call; the sessionStorage token from sign-in expires within the hour.
        setTemplatesAuthError('');
        const response = await apiClient.get('/get-templates');
        console.log('📡 Response status:', response.status);
        const data = response.data;
        console.log('✅ Templates received. Count:', data.templates?.length || 0);
        if (data.templates && Array.isArray(data.templates)) {
          setTemplates(data.templates);
          console.log('✅ Templates state updated');
        } else {
          console.error('❌ Invalid data structure:', data);
        }
      } catch (err) {
        console.error('❌ Error fetching templates:', err);
        // Only a refusal (401 / 403) gets its own message; any other failure keeps the old fallback.
        setTemplatesAuthError(templatesAuthMessage(err));
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  // Clear draft from localStorage
  const clearDraft = () => {
    localStorage.removeItem('ecm_campaign_draft_v2');
    setActiveStep(0);
    setSelectedTypes([]);
    setRecipientInputMethod('csv');
    setOwnRecipientList(false);
    setFile(null);
    setCampaignName('');
    setTemplateId('');
    setSurveyTemplateId('');
    setSurvey(null);
    setCanvasTitle('');
    setCanvasBody('');
    setCanvasNotifyStudents(true);
    setCanvasDelayPosting(false);
    setCanvasDelayPostAt('');
    setCanvasAlsoEmail(false);
    setFromEmail(fromAddresses[0].email);
    setFromName(fromAddresses[0].name);
    console.log('🗑️ Campaign draft cleared');
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleTemplateChange = async (templateId: string) => {
    setTemplateId(templateId);
    
    // Email subject is automatically fetched and set from template during campaign creation
    // The backend will retrieve it from SendGrid and store it in the campaign document
    if (templateId) {
      console.log('✅ Template selected:', templateId);
      console.log('ℹ️ Email subject will be automatically set from template during creation');
    }
  };

  /** Convert population recipients to a CSV File for upload. */
  const buildPopulationCsvFile = (recipients: PopulationRecipient[]): File => {
    const header = 'firstName,lastName,email';
    const rows = recipients.map(r =>
      `"${(r.firstName || '').replace(/"/g, '""')}","${(r.lastName || '').replace(/"/g, '""')}","${r.email}"`
    );
    const csv = [header, ...rows].join('\n');
    return new File([csv], 'population_recipients.csv', { type: 'text/csv' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!campaignName) {
      setError('Please enter a campaign name');
      return;
    }

    if (selectedTypes.length === 0) {
      setError('Please select at least one campaign type');
      return;
    }

    // Only require CSV file if recipient input method is 'csv', not in edit mode, and not saving as template
    if (!isEditMode && recipientInputMethod === 'csv' && !file && !saveAsTemplate) {
      setError('Please select a CSV file');
      return;
    }

    // Never submit without a successful fetch for the CURRENT population selection. A failed,
    // never-run, still-running or outdated fetch would otherwise fall through to a
    // distribution-list-only or stale (partial) send recorded under the selected populations.
    if (!isEditMode && recipientInputMethod === 'population' && selectedPopulations.length > 0 && !populationFetched && !saveAsTemplate) {
      setError(
        populationLoading
          ? 'Recipients are still being fetched. Wait for the list to finish loading before submitting.'
          : populationError
            ? 'Recipients for the selected population(s) could not be fetched, so there is no list to send to. Fetch them again successfully before submitting.'
            : 'Fetch recipients for the selected population(s) before submitting.'
      );
      return;
    }

    // Require at least one fetched recipient when using population method
    if (!isEditMode && recipientInputMethod === 'population' && populationRecipients.length === 0 && dlRecipients.length === 0 && !saveAsTemplate) {
      setError('Please fetch recipients from the selected population(s) or resolve a distribution list before submitting');
      return;
    }

    // For email campaigns, require template
    if (selectedTypes.includes('email') && !templateId) {
      setError('Please select a SendGrid template for the Email Campaign');
      return;
    }

    // For canvas announcement campaigns
    if (selectedTypes.includes('canvas_announcement') && !canvasTitle) {
      setError('Please enter an Announcement Title for the Canvas Announcement');
      return;
    }

    if (selectedTypes.includes('canvas_announcement') && !canvasBody) {
      setError('Please enter an Announcement Body for the Canvas Announcement');
      return;
    }

    // For Modo Push campaigns
    if (selectedTypes.includes('modo_push') && !modoTitle) {
      setError('Please enter a Push Notification Title for the Modo Push');
      return;
    }

    if (selectedTypes.includes('modo_push') && !modoMessage) {
      setError('Please enter a Push Notification Message for the Modo Push');
      return;
    }

    // For survey campaigns, require survey and template
    if (selectedTypes.includes('survey') && !survey) {
      setError('Please create a survey');
      return;
    }

    if (selectedTypes.includes('survey') && !surveyTemplateId) {
      setError('Please select a SendGrid template for the survey invitation email');
      return;
    }

    if (!fromEmail || !fromName) {
      setError('Please enter sender email and name');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // EDIT MODE: Update existing campaign
      if (isEditMode && editId) {
        const updateData = {
          title: campaignName,
          email_subject: templateId, // Backend will fetch from SendGrid
          email_template: templateId
        };
        
        await updateCampaign(editId, updateData);
        setSuccess(`✅ Campaign updated successfully! Redirecting to Campaigns...`);
        
        // Clear the draft after successful update
        clearDraft();
        
        // Redirect to ECM dashboard Campaigns tab after 2 seconds
        setTimeout(() => {
          navigate('/ECM/Dashboard');
        }, 2000);
        return;
      }
      
      // CREATE MODE: For survey campaigns, we'll store the survey structure
      if (selectedTypes.includes('survey')) {
        // Create campaign with survey data
        const surveyData = JSON.stringify(survey);
        
        // Only keep the last successful published survey as template
        // Clear all previous survey templates first
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('survey_')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Store this survey as the latest template
        localStorage.setItem('survey_last_template', surveyData);
        
        // FIRST: Save survey to Cosmos DB to get the survey_id and unique_url_slug
        console.log('📝 Creating survey in Cosmos DB first...');
        const surveyPayload = {
          title: survey!.title,
          description: survey!.description || undefined,
          closing_message: survey!.closing_message || undefined,
          unique_url_slug: survey!.unique_url_slug || undefined,
          is_anonymous: survey!.is_anonymous || false,
          allow_multiple_submissions: survey!.allow_multiple_submissions || false,
        };
        
        const surveyResponse = await apiClient.post('/surveys', surveyPayload);
        const createdSurvey = surveyResponse.data;
        console.log('✅ Survey created with ID:', createdSurvey.id);
        
        // Add questions to the survey if any
        console.log('📝 Survey has sections:', survey!.sections?.length || 0);
        let questionCount = 0;
        let failedCount = 0;
        let globalOrder = 0; // Track question order across all sections
        
        if (survey!.sections && survey!.sections.length > 0) {
          for (const section of survey!.sections) {
            console.log(`📝 Section "${section.title}" has questions:`, section.questions?.length || 0);
            
            if (section.questions && section.questions.length > 0) {
              for (const question of section.questions) {
                // SurveyBuilder uses 'questionText' field - declare outside try/catch
                const questionTitle = question.questionText || (question as any).question || (question as any).text || (question as any).title;
                
                // Convert options from string[] to QuestionOption[] format expected by backend
                let formattedOptions: Array<{id: string, text: string, value: string}> | undefined;
                if (question.options && Array.isArray(question.options)) {
                  formattedOptions = question.options.map((opt, idx) => ({
                    id: `opt-${Date.now()}-${idx}`,
                    text: opt,
                    value: opt
                  }));
                }
                
                // Use question's existing description if set (from subsections)
                // Otherwise fallback to section title
                let descriptionField = question.description;
                if (!descriptionField) {
                  descriptionField = section.title;
                  if (section.description) {
                    descriptionField += `|||${section.description}`;
                  }
                }
                
                // Map frontend question types to backend types
                const typeMapping: Record<string, string> = {
                  'radio': 'multiple-choice',
                  'select': 'dropdown',
                  'textblock': 'text',  // textblock renders as text display
                  // year and likert are sent as-is to backend (now supported)
                };
                const backendType = typeMapping[question.type] || question.type;
                
                try {
                  console.log('📝 Adding question:', questionTitle, 'type:', question.type, '→', backendType, 'options:', formattedOptions?.length || 0, 'description:', descriptionField?.substring(0, 30), 'followUp:', !!question.followUp);
                  await apiClient.post(`/surveys/${createdSurvey.id}/questions`, {
                    title: questionTitle,  // Backend expects 'title'
                    type: backendType,  // Use mapped backend type
                    required: question.required || false,
                    order: globalOrder++,  // Track question order
                    options: formattedOptions,  // Send formatted options
                    description: descriptionField,  // Include section title and description
                    followUp: question.followUp || undefined  // Include follow-up configuration
                  });
                  questionCount++;
                  console.log('✅ Question added successfully');
                } catch (err: any) {
                  failedCount++;
                  console.error('❌ Failed to add question:', questionTitle || 'unknown');
                  console.error('Error details:', err.response?.data || err.message);
                }
              }
            }
          }
        }
        console.log(`📝 Question summary: ${questionCount} added, ${failedCount} failed`);

        // Notify user if some questions failed to save
        if (failedCount > 0) {
          alert(`⚠️ Warning: ${failedCount} question(s) failed to save. Check browser console for details.`);
        }

        // Publish the survey so it's accessible
        await apiClient.put(`/surveys/${createdSurvey.id}`, {
          is_published: true
        });
        console.log('✅ Survey published');
        
        // Get template name from the selected template
        const selectedSurveyTemplate = templates.find(t => t.id === surveyTemplateId);
        const surveyTemplateName = selectedSurveyTemplate ? selectedSurveyTemplate.name : `Survey: ${survey!.title}`;
        
        // Only upload CSV if recipient input method is 'csv' or 'population'
        if ((recipientInputMethod === 'csv' && file) || (recipientInputMethod === 'population' && populationRecipients.length > 0)) {
          const uploadFile = recipientInputMethod === 'population' ? buildPopulationCsvFile(populationRecipients) : file!;
          const extraFields = recipientInputMethod === 'population' ? { populationSources: selectedPopulations.join(',') } : {};
          await uploadCampaign(uploadFile, campaignName, surveyTemplateId, surveyTemplateName, fromEmail, fromName, createdSurvey.id, saveAsTemplate, undefined, extraFields);
          setSuccess(`✅ Survey campaign ${saveAsTemplate ? 'template' : 'campaign'} created successfully! Redirecting to Campaigns...`);
        } else {
          // Manual input - no CSV upload, just create the campaign metadata
          setSuccess(`✅ Survey campaign created successfully (no recipients)! Redirecting to Campaigns...`);
        }
      } else if (selectedTypes.includes('canvas_announcement') && !selectedTypes.includes('survey')) {
        // Canvas Announcement campaign (without survey)
        // Only upload CSV if recipient input method is 'csv' or 'population'
        if ((recipientInputMethod === 'csv' && file) || (recipientInputMethod === 'population' && populationRecipients.length > 0)) {
          const uploadFile = recipientInputMethod === 'population' ? buildPopulationCsvFile(populationRecipients) : file!;
          const formData = new FormData();
          formData.append('file', uploadFile);
          formData.append('campaignName', campaignName);
          formData.append('templateId', templateId || 'canvas-only');
          formData.append('templateName', canvasTitle);
          formData.append('fromEmail', fromEmail);
          formData.append('fromName', fromName);
          formData.append('campaignType', 'canvas_announcement');
          formData.append('canvasTitle', canvasTitle);
          formData.append('canvasBody', canvasBody);
          formData.append('canvasNotifyStudents', String(canvasNotifyStudents));
          formData.append('canvasAlsoEmail', String(canvasAlsoEmail));
          if (canvasDelayPosting && canvasDelayPostAt) {
            formData.append('canvasDelayPostAt', canvasDelayPostAt);
          }
          if (saveAsTemplate) formData.append('isTemplate', 'true');
          if (recipientInputMethod === 'population') formData.append('populationSources', selectedPopulations.join(','));
          await apiClient.post('/upload-campaign', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          setSuccess(`✅ Canvas Announcement ${saveAsTemplate ? 'template' : 'campaign'} created successfully! Redirecting to Campaigns...`);
        } else {
          setSuccess(`✅ Canvas Announcement created successfully (no recipients)! Redirecting to Campaigns...`);
        }
      } else if (selectedTypes.includes('modo_push') && !selectedTypes.includes('survey')) {
        // Modo Push campaign
        if ((recipientInputMethod === 'csv' && file) || (recipientInputMethod === 'population' && populationRecipients.length > 0)) {
          const uploadFile = recipientInputMethod === 'population' ? buildPopulationCsvFile(populationRecipients) : file!;
          const formData = new FormData();
          formData.append('file', uploadFile);
          formData.append('campaignName', campaignName);
          formData.append('templateId', templateId || 'modo-only');
          formData.append('templateName', modoTitle);
          formData.append('fromEmail', fromEmail);
          formData.append('fromName', fromName);
          formData.append('campaignType', 'modo_push');
          formData.append('modoTitle', modoTitle);
          formData.append('modoMessage', modoMessage);
          formData.append('modoNotifyPush', String(modoNotifyPush));
          formData.append('modoAlsoEmail', String(modoAlsoEmail));
          formData.append('modoDeepLinkUrl', modoDeepLinkUrl);
          formData.append('modoAudience', deriveModoAudience(selectedPopulations));
          formData.append('modoEnvironment', modoEnvironment);
          if (modoDelay && modoScheduleAt) {
            formData.append('modoScheduleAt', modoScheduleAt);
          }
          if (saveAsTemplate) formData.append('isTemplate', 'true');
          if (recipientInputMethod === 'population') formData.append('populationSources', selectedPopulations.join(','));
          await apiClient.post('/upload-campaign', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          setSuccess(`✅ Modo Push ${saveAsTemplate ? 'template' : 'campaign'} created successfully! Redirecting to Campaigns...`);
        } else {
          setSuccess(`✅ Modo Push created successfully (no recipients)! Redirecting to Campaigns...`);
        }
      } else {
        // Regular email campaign (or email + canvas combination)
        const selectedTemplate = templates.find(t => t.id === templateId);
        const templateName = selectedTemplate ? selectedTemplate.name : 'Unknown Template';
        const campaignTypeStr = selectedTypes.join(',');

        // Merge distribution list recipients with population recipients (deduped by email)
        const mergedPopRecipients = [...populationRecipients];
        const seenMerged = new Set(populationRecipients.map(r => r.email.toLowerCase()));
        for (const r of dlRecipients) {
          if (!seenMerged.has(r.email.toLowerCase())) {
            seenMerged.add(r.email.toLowerCase());
            mergedPopRecipients.push(r);
          }
        }

        // Only upload CSV if recipient input method is 'csv' or 'population'
        if ((recipientInputMethod === 'csv' && file) || (recipientInputMethod === 'population' && mergedPopRecipients.length > 0)) {
          const uploadFile = recipientInputMethod === 'population' ? buildPopulationCsvFile(mergedPopRecipients) : file!;
          const extraFields = recipientInputMethod === 'population' ? { populationSources: selectedPopulations.join(',') } : {};
          await uploadCampaign(uploadFile, campaignName, templateId, templateName, fromEmail, fromName, undefined, saveAsTemplate, campaignTypeStr, extraFields);
          setSuccess(`✅ Campaign ${saveAsTemplate ? 'template' : 'campaign'} created successfully! Redirecting to Campaigns...`);
        } else {
          // Manual input - no CSV upload
          setSuccess(`✅ Campaign created successfully (no recipients)! Redirecting to Campaigns...`);
        }
      }
      
      // Clear the draft after successful submission
      clearDraft();

      // Auto-save the sender pair to the address book if not already present
      try {
        const currentBook: FromAddress[] = JSON.parse(localStorage.getItem('ecm_from_addresses') || '[]');
        if (!currentBook.some(a => a.email.toLowerCase() === fromEmail.toLowerCase())) {
          const updated = [...currentBook, { name: fromName, email: fromEmail }];
          localStorage.setItem('ecm_from_addresses', JSON.stringify(updated));
          setFromAddresses(updated);
        }
      } catch (_) { /* non-critical */ }

      // Redirect to ECM dashboard Campaigns tab after 1.5 seconds
      console.log('🔄 Scheduling redirect to Campaigns tab...');
      setTimeout(() => {
        console.log('🔄 Redirecting to Campaigns tab now');
        navigate('/ECM/Dashboard');
      }, 1500);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || (isEditMode ? 'Failed to update campaign' : 'Failed to create campaign');
      setError(errorMessage);
      console.error(`Campaign ${isEditMode ? 'update' : 'creation'} failed:`, errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setFile(null);
    setCampaignName('');
    setTemplateId('');
    setSurveyTemplateId('');
    setSurvey(null);
    setActiveStep(0);
    setFromName('Laurus College Academics');
    setFromEmail('academics@lauruscollege.edu');
    setDlEmail('');
    setDlRecipients([]);
    setDlLoading(false);
    setDlResolved(false);
    setDlGroupName('');
    setError('');
    setSuccess('');
    setLoadingTemplates(true);
    
    // Re-fetch templates
    const fetchTemplates = async () => {
      try {
        console.log('🔄 Refreshing templates from: /get-templates');
        setTemplatesAuthError('');
        const response = await apiClient.get('/get-templates');   // fresh MSAL token, as on mount
        console.log('📡 Response status:', response.status);
        const data = response.data;
        console.log('✅ Templates received. Count:', data.templates?.length || 0);
        if (data.templates && Array.isArray(data.templates)) {
          setTemplates(data.templates);
          console.log('✅ Templates state updated');
        } else {
          console.error('❌ Invalid data structure:', data);
          setTemplates([]);
        }
      } catch (err) {
        console.error('❌ Error fetching templates:', err);
        setTemplatesAuthError(templatesAuthMessage(err));
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  };

  const handleSaveSurvey = async (surveyData: Survey) => {
    try {
      console.log('💾 Saving survey to local state...', surveyData);
      
      // During campaign creation, we DON'T create the survey here
      // The survey is created later in handleSubmit when the full campaign is submitted
      // This prevents duplicate survey creation
      
      // Just save to local state and localStorage
      setSurvey(surveyData);
      setCampaignName(surveyData.title);
      
      // Save to localStorage for template persistence
      const surveyJson = JSON.stringify(surveyData);
      localStorage.setItem('survey_last_template', surveyJson);
      
      console.log('✅ Survey saved to local state (will be created when campaign is submitted)');
      
      // Don't make any API calls - survey creation happens in handleSubmit
    } catch (error: any) {
      console.error('❌ Failed to save survey:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to save survey: ${error.response?.data?.error || error.message}`);
      throw error; // Re-throw so SurveyBuilder knows it failed
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start',
      minHeight: 'calc(100vh - 120px)',
      p: 3,
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      // Override MUI Paper default styles globally within this component
      '& .MuiPaper-root': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiTextField-root input, & .MuiTextField-root textarea': {
        color: 'var(--text-primary)'
      },
      '& .MuiSelect-select': {
        color: 'var(--text-primary)'
      }
    }}>
      <Box sx={{ width: '100%', maxWidth: 1200 }}>
        {loadingCampaign ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: 'var(--accent)' }} />
          </Box>
        ) : (
          <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ color: 'var(--accent)' }}>
            {isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
          </Typography>
          <IconButton 
            onClick={handleRefresh}
            title="Refresh form and reload templates"
            sx={{ ml: 1, color: 'var(--accent)' }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Draft indicator and clear button */}
        {(campaignName || templateId || surveyTemplateId || survey || canvasTitle || selectedTypes.length > 0) && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearDraft}>
                Clear Draft
              </Button>
            }
          >
            💾 Draft saved - your progress will be preserved if you refresh the page
          </Alert>
        )}

        <Paper sx={{ p: 4, backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}>
          <Stepper 
            activeStep={activeStep} 
            orientation="vertical"
            sx={{
              '& .MuiStepIcon-root': {
                color: 'var(--text-secondary)',
                '&.Mui-active': {
                  color: 'var(--accent)',
                },
                '&.Mui-completed': {
                  color: 'var(--success)',
                },
              },
              '& .MuiStepIcon-text': {
                fill: 'var(--card-bg)',
              },
              '& .MuiStepLabel-label': {
                color: 'var(--text-primary) !important',
                '&.Mui-active': {
                  color: 'var(--text-primary) !important',
                  fontWeight: 500,
                },
                '&.Mui-completed': {
                  color: 'var(--text-primary) !important',
                },
              },
            }}
          >
            {/* Step 1: Choose Campaign Type */}
            <Step>
              <StepLabel>Choose Campaign Type</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph sx={{ backgroundColor: 'rgba(128, 128, 128, 0.1)', p: 1.5, borderRadius: 1 }}>
                  Select one or more campaign types. You can combine multiple channels for the same campaign.
                </Typography>
                {selectedTypes.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Please select at least one campaign type to continue.
                  </Alert>
                )}
                <Stack spacing={2}>
                  {/* Email Campaign Card */}
                  <Paper
                    elevation={selectedTypes.includes('email') ? 8 : 2}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: selectedTypes.includes('email') ? '2px solid' : '1px solid',
                      borderColor: selectedTypes.includes('email') ? 'primary.main' : 'divider',
                      backgroundColor: selectedTypes.includes('email') ? 'primary.50' : 'background.paper',
                      position: 'relative',
                      '&:hover': { elevation: 4 }
                    }}
                    onClick={() => toggleType('email')}
                  >
                    {selectedTypes.includes('email') && (
                      <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'primary.main', fontSize: 24 }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <EmailIcon sx={{ fontSize: 40, color: 'var(--accent)' }} />
                      <Box>
                        <Typography variant="h6">Email Campaign</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send emails using SendGrid templates to your recipients
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Survey Campaign Card */}
                  <Paper
                    elevation={selectedTypes.includes('survey') ? 8 : 2}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: selectedTypes.includes('survey') ? '2px solid' : '1px solid',
                      borderColor: selectedTypes.includes('survey') ? 'secondary.main' : 'divider',
                      backgroundColor: selectedTypes.includes('survey') ? 'secondary.50' : 'background.paper',
                      position: 'relative',
                      '&:hover': { elevation: 4 }
                    }}
                    onClick={() => toggleType('survey')}
                  >
                    {selectedTypes.includes('survey') && (
                      <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'secondary.main', fontSize: 24 }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PollIcon sx={{ fontSize: 40, color: 'var(--accent)' }} />
                      <Box>
                        <Typography variant="h6">Survey Campaign</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create a custom survey with flexible questions and sections
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Canvas Announcement Card */}
                  <Paper
                    elevation={selectedTypes.includes('canvas_announcement') ? 8 : 2}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: selectedTypes.includes('canvas_announcement') ? '2px solid' : '1px solid',
                      borderColor: selectedTypes.includes('canvas_announcement') ? 'success.main' : 'divider',
                      backgroundColor: selectedTypes.includes('canvas_announcement') ? 'success.50' : 'background.paper',
                      position: 'relative',
                      '&:hover': { elevation: 4 }
                    }}
                    onClick={() => toggleType('canvas_announcement')}
                  >
                    {selectedTypes.includes('canvas_announcement') && (
                      <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'success.main', fontSize: 24 }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AnnouncementIcon sx={{ fontSize: 40, color: 'var(--accent)' }} />
                      <Box>
                        <Typography variant="h6">Canvas Announcement</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Post announcements to Canvas courses for your recipients
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Modo Push Card */}
                  <Paper
                    elevation={selectedTypes.includes('modo_push') ? 8 : 2}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: selectedTypes.includes('modo_push') ? '2px solid' : '1px solid',
                      borderColor: selectedTypes.includes('modo_push') ? 'warning.main' : 'divider',
                      backgroundColor: selectedTypes.includes('modo_push') ? 'warning.50' : 'background.paper',
                      position: 'relative',
                      '&:hover': { elevation: 4 }
                    }}
                    onClick={() => toggleType('modo_push')}
                  >
                    {selectedTypes.includes('modo_push') && (
                      <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, color: 'warning.main', fontSize: 24 }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <NotificationsActiveIcon sx={{ fontSize: 40, color: 'var(--accent)' }} />
                      <Box>
                        <Typography variant="h6">Modo Push</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send push notifications through Modolabs to desktop, tablet and mobile
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {selectedTypes.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Selected: {selectedTypes.map(t =>
                        t === 'email' ? 'Email Campaign' :
                        t === 'survey' ? 'Survey Campaign' :
                        t === 'canvas_announcement' ? 'Canvas Announcement' :
                        'Modo Push'
                      ).join(' + ')}
                    </Alert>
                  )}

                  {/* ── Modo Environment Selector (shown only for Modo Push) ── */}
                  {selectedTypes.includes('modo_push') && (
                    <>
                      <Divider sx={{ pt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', px: 1 }}>
                          MODO ENVIRONMENT
                        </Typography>
                      </Divider>
                      <Typography variant="body2" sx={{ backgroundColor: 'rgba(128,128,128,0.1)', p: 1.5, borderRadius: 1 }}>
                        Select which Modo environment this push notification will be sent to.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {([
                          { key: 'test',       label: 'Test',       description: 'Sends via test token & test channels',       color: 'info' },
                          { key: 'production', label: 'Production', description: 'Sends via production token & live channels', color: 'error' },
                        ] as const).map(({ key, label, description, color }) => {
                          const selected = modoEnvironment === key;
                          return (
                            <Paper
                              key={key}
                              elevation={selected ? 8 : 2}
                              onClick={() => setModoEnvironment(key)}
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                flex: 1,
                                border: selected ? '2px solid' : '1px solid',
                                borderColor: selected ? `${color}.main` : 'divider',
                                backgroundColor: selected ? `${color}.50` : 'background.paper',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {selected && (
                                <CheckCircleIcon sx={{ position: 'absolute', top: 6, right: 6, color: `${color}.main`, fontSize: 18 }} />
                              )}
                              <Typography variant="body1" fontWeight={selected ? 700 : 400}>
                                {label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" align="center">
                                {description}
                              </Typography>
                            </Paper>
                          );
                        })}
                      </Box>
                      {modoEnvironment === 'production' && (
                        <Alert severity="warning" sx={{ mt: 0.5 }}>
                          <strong>Production selected</strong> — this push will reach real users on live Modo channels.
                        </Alert>
                      )}
                    </>
                  )}

                  {/* ── Population Selection ── */}
                  <Divider sx={{ pt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', px: 1 }}>
                      TARGET POPULATION
                    </Typography>
                  </Divider>
                  <Typography variant="body2" sx={{ backgroundColor: 'rgba(128,128,128,0.1)', p: 1.5, borderRadius: 1 }}>
                    Who should receive this campaign? Select one or more groups.
                  </Typography>

                  {selectedPopulations.length === 0 && dlRecipients.length === 0 && (
                    <Alert severity="warning">Please select at least one target population or resolve a distribution list to continue.</Alert>
                  )}

                  {/* Population cards — row layout */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {([
                      { key: 'current_students',   label: 'Current Students',   Icon: SchoolIcon,            color: 'primary' },
                      { key: 'withdrawn_students',  label: 'Withdrawn Students', Icon: PersonOffIcon,         color: 'warning' },
                      { key: 'alumni',              label: 'Alumni',             Icon: WorkspacePremiumIcon,  color: 'secondary' },
                      { key: 'faculty',             label: 'All Personnel',      Icon: GroupsIcon,            color: 'success' },
                      { key: 'it_systems_devops',   label: 'IT Systems & DevOps', Icon: TerminalIcon,         color: 'error' },
                    ] as const).map(({ key, label, Icon, color }) => {
                      const selected = selectedPopulations.includes(key as PopulationType);
                      return (
                        <Paper
                          key={key}
                          elevation={selected ? 8 : 2}
                          onClick={() => togglePopulation(key as PopulationType)}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            minWidth: 130,
                            border: selected ? '2px solid' : '1px solid',
                            borderColor: selected ? `${color}.main` : 'divider',
                            backgroundColor: selected ? `${color}.50` : 'background.paper',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { elevation: 4 },
                          }}
                        >
                          {selected && (
                            <CheckCircleIcon sx={{ position: 'absolute', top: 6, right: 6, color: `${color}.main`, fontSize: 18 }} />
                          )}
                          <Icon sx={{ fontSize: 32, color: selected ? `${color}.main` : 'var(--text-secondary)' }} />
                          <Typography variant="body2" fontWeight={selected ? 600 : 400} align="center">
                            {label}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>

                  {selectedPopulations.length > 0 && (
                    <Alert severity="success" icon={<PeopleIcon />} sx={{ mt: 0.5 }}>
                      Targeting: <strong>{selectedPopulations.map(p =>
                        p === 'current_students' ? 'Current Students' :
                        p === 'withdrawn_students' ? 'Withdrawn Students' :
                        p === 'alumni' ? 'Alumni' :
                        p === 'faculty' ? 'All Personnel' :
                        'IT Systems & DevOps'
                      ).join(', ')}</strong>
                    </Alert>
                  )}

                  {/* ── Distribution List (email campaigns only) ── */}
                  {selectedTypes.includes('email') && (
                    <>
                      <Divider sx={{ pt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', px: 1 }}>
                          DISTRIBUTION LIST (optional)
                        </Typography>
                      </Divider>
                      <Typography variant="body2" sx={{ backgroundColor: 'rgba(128,128,128,0.1)', p: 1.5, borderRadius: 1 }}>
                        Alternatively (or additionally), add recipients from an Outlook distribution list.
                        Members will be merged with any selected populations above.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          label="Distribution List Email"
                          placeholder="e.g. dl-faculty@lauruscollege.edu"
                          value={dlEmail}
                          onChange={(e) => {
                            setDlEmail(e.target.value);
                            setDlResolved(false);
                            setDlRecipients([]);
                            setDlGroupName('');
                          }}
                          size="small"
                          sx={{ flex: 1 }}
                          helperText={
                            dlResolved && dlGroupName
                              ? `Resolved: "${dlGroupName}" — ${dlRecipients.length} member${dlRecipients.length !== 1 ? 's' : ''}`
                              : 'Enter the Outlook DL email address'
                          }
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!dlEmail.trim() || dlLoading}
                          startIcon={dlLoading ? <CircularProgress size={14} /> : <AlternateEmailIcon />}
                          onClick={async () => {
                            setDlLoading(true);
                            setDlResolved(false);
                            setError('');
                            try {
                              const result = await resolveDistributionList(dlEmail.trim().toLowerCase());
                              setDlRecipients(result.members);
                              setDlGroupName(result.groupName);
                              setDlResolved(true);
                              // Auto-set recipient method to population so submit merges correctly
                              setRecipientInputMethod('population');
                              setOwnRecipientList(false);
                            } catch (err: any) {
                              setError(
                                err.response?.data?.error ||
                                'Failed to resolve distribution list. Check the email address and try again.'
                              );
                            } finally {
                              setDlLoading(false);
                            }
                          }}
                          sx={{ mt: 0.25, minWidth: 110 }}
                        >
                          {dlLoading ? 'Resolving…' : dlResolved ? 'Re-resolve' : 'Resolve'}
                        </Button>
                      </Box>
                      {dlResolved && (
                        <Alert severity={dlRecipients.length > 0 ? 'success' : 'warning'} icon={<AlternateEmailIcon />}>
                          {dlRecipients.length > 0
                            ? `${dlRecipients.length} member${dlRecipients.length !== 1 ? 's' : ''} found in "${dlGroupName}"`
                            : `No members found in "${dlGroupName}"`}
                        </Alert>
                      )}
                    </>
                  )}

                  <Divider sx={{ my: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      OR PROVIDE YOUR OWN LIST
                    </Typography>
                  </Divider>

                  <Paper
                    elevation={ownRecipientList ? 8 : 2}
                    sx={{
                      p: 2.5,
                      cursor: 'pointer',
                      border: ownRecipientList ? '2px solid' : '1px solid',
                      borderColor: ownRecipientList ? 'primary.main' : 'divider',
                      backgroundColor: ownRecipientList ? 'primary.50' : 'background.paper',
                    }}
                    onClick={() => {
                      const next = !ownRecipientList;
                      setOwnRecipientList(next);
                      if (next) {
                        // One recipient source at a time. Leaving a population or a resolved
                        // distribution list selected would silently merge them into the upload
                        // at submit, which is exactly the surprise this option exists to avoid.
                        setSelectedPopulations([]);
                        populationRequestId.current += 1;
                        setPopulationRecipients([]);
                        setPopulationFetched(false);
                        setPopulationLoading(false);
                        setPopulationError('');
                        setDlEmail('');
                        setDlRecipients([]);
                        setDlResolved(false);
                        setDlGroupName('');
                        setRecipientInputMethod('csv');
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CloudUploadIcon sx={{ fontSize: 32, color: ownRecipientList ? 'primary.main' : 'var(--accent)' }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          I'll provide the recipient list myself
                          {ownRecipientList && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                              &#10003; selected
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Neither of the above fits this campaign. You&apos;ll upload a CSV of
                          recipients in step 3. Choosing this clears any groups or distribution
                          list selected above.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <Button onClick={() => navigate('/ECM/Dashboard')}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        // Auto-select population method when populations or DL are chosen
                        if (ownRecipientList) {
                          setRecipientInputMethod('csv');
                        } else if (selectedPopulations.length > 0 || dlRecipients.length > 0) {
                          setRecipientInputMethod('population');
                        }
                        setActiveStep(1);
                      }}
                      disabled={selectedTypes.length === 0 || (!ownRecipientList && selectedPopulations.length === 0 && dlRecipients.length === 0)}
                    >
                      Continue
                    </Button>
                  </Box>
                </Stack>
              </StepContent>
            </Step>

            {/* Step 2: Campaign Details */}
            <Step>
              <StepLabel>
                {selectedTypes.includes('survey') && !survey ? 'Build Your Survey' : 'Campaign Details'}
              </StepLabel>
              <StepContent>
                {/* Show SurveyBuilder first if survey is selected and not yet built */}
                {selectedTypes.includes('survey') && !survey ? (
                  <SurveyBuilder
                    initialSurvey={survey || undefined}
                    onSave={handleSaveSurvey}
                    onCancel={() => setActiveStep(0)}
                  />
                ) : (
                  <Stack spacing={3}>
                    {/* Campaign Name — always shown */}
                    <TextField
                      label="Campaign Name"
                      value={campaignName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCampaignName(e.target.value)}
                      required
                      fullWidth
                      helperText="Give this campaign a descriptive name"
                      autoFocus
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={saveAsTemplate}
                          onChange={(e) => setSaveAsTemplate(e.target.checked)}
                          sx={{ color: 'var(--accent)' }}
                        />
                      }
                      label="Save as reusable template (no emails will be sent)"
                      sx={{ color: 'var(--text-primary)' }}
                    />

                    {/* ── Email Campaign Settings ── */}
                    {selectedTypes.includes('email') && (
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" /> Email Campaign Settings
                        </Typography>
                        {loadingTemplates ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={20} />
                            <Typography>Loading SendGrid templates...</Typography>
                          </Box>
                        ) : (
                          <FormControl fullWidth required>
                            <InputLabel>SendGrid Template *</InputLabel>
                            <Select
                              value={templateId}
                              onChange={(e) => handleTemplateChange(e.target.value)}
                              label="SendGrid Template *"
                            >
                              {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                  {template.name} ({template.id})
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error={!!templatesAuthError}>
                              {templatesAuthError
                                ? templatesAuthError
                                : templates.length > 0
                                ? `Select a SendGrid dynamic template (${templates.length} available)`
                                : 'No templates found. Please check SendGrid configuration.'}
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Box>
                    )}

                    {/* ── Survey Settings (after survey is built) ── */}
                    {selectedTypes.includes('survey') && survey && (
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PollIcon fontSize="small" /> Survey Settings
                        </Typography>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          ✅ Survey "{survey.title}" created with {survey.sections.length} section(s) and {survey.sections.reduce((total, s) => total + s.questions.length, 0)} question(s)
                        </Alert>
                        <Button variant="outlined" size="small" onClick={() => setSurvey(null)} sx={{ mb: 2 }}>
                          Edit Survey
                        </Button>

                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                          📧 Survey Invitation Email Template
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ backgroundColor: 'rgba(128, 128, 128, 0.1)', p: 1.5, borderRadius: 1 }}>
                          Select the SendGrid template for sending survey invitation emails.
                        </Typography>
                        {loadingTemplates ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={20} />
                            <Typography>Loading templates...</Typography>
                          </Box>
                        ) : (
                          <FormControl fullWidth required>
                            <InputLabel>SendGrid Template for Survey Invitation *</InputLabel>
                            <Select
                              value={surveyTemplateId}
                              onChange={(e) => setSurveyTemplateId(e.target.value)}
                              label="SendGrid Template for Survey Invitation *"
                            >
                              {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                  {template.name} ({template.id})
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error={!!templatesAuthError}>
                              {templatesAuthError
                                ? templatesAuthError
                                : templates.length > 0
                                ? `Select the email template for survey invitations (${templates.length} available)`
                                : 'No templates found. Please check SendGrid configuration.'}
                            </FormHelperText>
                          </FormControl>
                        )}
                        <Alert severity="info" sx={{ mt: 1 }}>
                          💡 Template should include <code>{'{{student_name}}'}</code>, <code>{'{{survey_title}}'}</code>, and <code>{'{{survey_url}}'}</code>.
                        </Alert>
                      </Box>
                    )}

                    {/* ── Canvas Announcement Settings ── */}
                    {selectedTypes.includes('canvas_announcement') && (
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AnnouncementIcon fontSize="small" /> Canvas Announcement Settings
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            label="Announcement Title *"
                            value={canvasTitle}
                            onChange={(e) => setCanvasTitle(e.target.value)}
                            required
                            fullWidth
                            helperText="The title/heading of the Canvas announcement"
                          />

                          <TextField
                            label="Announcement Body *"
                            value={canvasBody}
                            onChange={(e) => setCanvasBody(e.target.value)}
                            required
                            fullWidth
                            multiline
                            minRows={4}
                            helperText="The body text of the announcement. HTML is supported (e.g. <b>bold</b>, <a href='...'>link</a>)."
                          />

                          <Alert severity="info">
                            <Typography variant="body2">
                              <strong>Course Targeting:</strong> To post to specific courses, include a <code>course_id</code> column in your CSV. Each row can target a different course. For a single course, all rows should share the same <code>course_id</code>.
                            </Typography>
                          </Alert>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={canvasNotifyStudents}
                                onChange={(e) => setCanvasNotifyStudents(e.target.checked)}
                                sx={{ color: 'var(--accent)' }}
                              />
                            }
                            label="Notify students via push notification when announcement is posted"
                            sx={{ color: 'var(--text-primary)' }}
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={canvasAlsoEmail}
                                onChange={(e) => setCanvasAlsoEmail(e.target.checked)}
                                sx={{ color: 'var(--accent)' }}
                              />
                            }
                            label='Also send email — "New Message in Canvas" with a link directly to the announcement'
                            sx={{ color: 'var(--text-primary)' }}
                          />

                          {canvasAlsoEmail && (
                            <Alert
                              severity="info"
                              sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                📧 Branded email will be sent automatically
                              </Typography>
                              <Typography variant="body2">
                                Each recipient will receive a Laurus College branded email with the subject <strong>"New Message in Canvas"</strong> containing the announcement title and a direct link to the Canvas post. Uses the <strong>From Address</strong> set below — no SendGrid template required.
                              </Typography>
                            </Alert>
                          )}

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={canvasDelayPosting}
                                onChange={(e) => setCanvasDelayPosting(e.target.checked)}
                                sx={{ color: 'var(--accent)' }}
                              />
                            }
                            label="Delay posting (schedule for a future date/time)"
                            sx={{ color: 'var(--text-primary)' }}
                          />

                          {canvasDelayPosting && (
                            <TextField
                              label="Post At (Date & Time) *"
                              type="datetime-local"
                              value={canvasDelayPostAt}
                              onChange={(e) => setCanvasDelayPostAt(e.target.value)}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              helperText="The announcement will be posted at this date and time"
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* ── Modo Push Settings ── */}
                    {selectedTypes.includes('modo_push') && (
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <NotificationsActiveIcon fontSize="small" /> Modo Push Settings
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            label="Push Notification Title *"
                            value={modoTitle}
                            onChange={(e) => setModoTitle(e.target.value)}
                            required
                            fullWidth
                            helperText="The headline shown on the push notification banner"
                          />

                          <TextField
                            label="Push Notification Message *"
                            value={modoMessage}
                            onChange={(e) => setModoMessage(e.target.value)}
                            required
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="The body text displayed beneath the title in the notification"
                          />

                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>📡 Modo Channel (auto-selected from Environment + Target Population)</Typography>
                            <Typography variant="body2">
                              {(() => {
                                const derived = deriveModoAudience(selectedPopulations);
                                const env = modoEnvironment === 'production' ? 'Production' : 'Test';
                                if (derived === 'employees') return `👔 ${env} Employee channel — Faculty, Staff & IT Systems`;
                                if (derived === 'students')  return `🎓 ${env} Student channel — Current Students, Withdrawn & Alumni`;
                                return `🌐 ${env} General channel — all 6 populations selected`;
                              })()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Channel is determined by your Environment selection and Target Population.
                            </Typography>
                          </Alert>

                          <TextField
                            label="Deep Link URL (optional)"
                            value={modoDeepLinkUrl}
                            onChange={(e) => setModoDeepLinkUrl(e.target.value)}
                            fullWidth
                            helperText="Link that opens when the user taps the notification (e.g. a Modo content page URL)"
                            placeholder="https://your-campus.modolabs.net/..."
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={modoNotifyPush}
                                onChange={(e) => setModoNotifyPush(e.target.checked)}
                                sx={{ color: 'var(--accent)' }}
                              />
                            }
                            label="Notify via push notification (deliver to desktop, tablet and mobile)"
                            sx={{ color: 'var(--text-primary)' }}
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={modoAlsoEmail}
                                onChange={(e) => setModoAlsoEmail(e.target.checked)}
                                sx={{ color: 'var(--accent)' }}
                              />
                            }
                            label='Also send email — "New Message from Modo" with a link directly to the content'
                            sx={{ color: 'var(--text-primary)' }}
                          />

                          {modoAlsoEmail && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                📧 Branded email will be sent automatically
                              </Typography>
                              <Typography variant="body2">
                                Each recipient receives a Laurus College branded email with subject <strong>"New Message from Modo"</strong> containing the notification title and a direct link to the content. Uses the <strong>From Address</strong> set below — no SendGrid template required.
                              </Typography>
                            </Alert>
                          )}

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={modoDelay}
                                onChange={(e) => setModoDelay(e.target.checked)}
                                sx={{ color: 'var(--accent)' }}
                              />
                            }
                            label="Delay posting (schedule for a future date/time)"
                            sx={{ color: 'var(--text-primary)' }}
                          />

                          {modoDelay && (
                            <TextField
                              label="Send At (Date & Time) *"
                              type="datetime-local"
                              value={modoScheduleAt}
                              onChange={(e) => setModoScheduleAt(e.target.value)}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              helperText="The push notification will be sent at this date and time"
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* Continue / Back buttons */}
                    <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                      <Button onClick={() => setActiveStep(0)}>Back</Button>
                      <Button
                        variant="contained"
                        onClick={() => setActiveStep(2)}
                        disabled={
                          !campaignName ||
                          (selectedTypes.includes('email') && !templateId) ||
                          (selectedTypes.includes('survey') && !surveyTemplateId) ||
                          (selectedTypes.includes('canvas_announcement') && (!canvasTitle || !canvasBody || (canvasDelayPosting && !canvasDelayPostAt))) ||
                          (selectedTypes.includes('modo_push') && (!modoTitle || !modoMessage || (modoDelay && !modoScheduleAt)))
                        }
                      >
                        Continue to Recipients
                      </Button>
                    </Box>
                  </Stack>
                )}
              </StepContent>
            </Step>

            {/* Step 3: Recipients & Sender Info */}
            <Step>
              <StepLabel>Recipients & Sender Information</StepLabel>
              <StepContent>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    {selectedTypes.includes('survey') && survey && (
                      <Alert severity="success">
                        Survey "{survey.title}" is ready with {survey.sections.length} section(s)
                      </Alert>
                    )}

                    <Box>
                      <Typography variant="body2" paragraph color="text.primary">
                        How would you like to input recipient information?
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--accent)' }}>
                        v2.0 - Updated {new Date().toLocaleDateString()}
                      </Typography>
                      <Stack spacing={2} sx={{ mb: 3 }}>
                        <Paper
                          elevation={recipientInputMethod === 'csv' ? 8 : 2}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: recipientInputMethod === 'csv' ? '2px solid' : '1px solid',
                            borderColor: recipientInputMethod === 'csv' ? 'primary.main' : 'divider',
                            backgroundColor: recipientInputMethod === 'csv' ? 'primary.50' : 'background.paper',
                            '&:hover': { elevation: 4 }
                          }}
                          onClick={() => setRecipientInputMethod('csv')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CloudUploadIcon sx={{ fontSize: 40, color: 'var(--accent)' }} />
                            <Box>
                              <Typography variant="h6">Upload CSV File</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Bulk import recipients with email addresses and custom fields
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>

                        {!(selectedTypes.length === 1 && selectedTypes.includes('canvas_announcement')) && (
                          <Paper
                            elevation={recipientInputMethod === 'manual' ? 8 : 2}
                            sx={{
                              p: 3,
                              cursor: 'pointer',
                              border: recipientInputMethod === 'manual' ? '2px solid' : '1px solid',
                              borderColor: recipientInputMethod === 'manual' ? 'secondary.main' : 'divider',
                              backgroundColor: recipientInputMethod === 'manual' ? 'secondary.50' : 'background.paper',
                              '&:hover': { elevation: 4 }
                            }}
                            onClick={() => setRecipientInputMethod('manual')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <EmailIcon sx={{ fontSize: 40, color: 'var(--accent)' }} />
                              <Box>
                                <Typography variant="h6">Manual Input</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Sender information only (for surveys without recipient list)
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        )}

                        {/* Population recipient method — auto-highlighted when populations are selected */}
                        <Paper
                          elevation={recipientInputMethod === 'population' ? 8 : 2}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: recipientInputMethod === 'population' ? '2px solid' : '1px solid',
                            borderColor: recipientInputMethod === 'population' ? 'success.main' : 'divider',
                            backgroundColor: recipientInputMethod === 'population' ? 'success.50' : 'background.paper',
                            '&:hover': { elevation: 4 }
                          }}
                          onClick={() => setRecipientInputMethod('population')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <PeopleIcon sx={{ fontSize: 40, color: recipientInputMethod === 'population' ? 'success.main' : 'var(--accent)' }} />
                            <Box>
                              <Typography variant="h6">
                                Fetch from Population
                                {selectedPopulations.length > 0 && (
                                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.main', fontWeight: 600 }}>
                                    ✓ {selectedPopulations.length} group{selectedPopulations.length > 1 ? 's' : ''} selected
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Auto-populate recipients from your chosen population group(s)
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Stack>
                    </Box>

                    {/* ── Population fetch & preview ── */}
                    {recipientInputMethod === 'population' && (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Population Recipients
                        </Typography>
                        <Stack spacing={2}>
                          <Alert severity="info">
                            Fetching from: <strong>{selectedPopulations.map(p =>
                              p === 'current_students' ? 'Current Students' :
                              p === 'withdrawn_students' ? 'Withdrawn Students' :
                              p === 'alumni' ? 'Alumni' :
                              p === 'faculty' ? 'All Personnel' :
                              'IT Systems & DevOps'
                            ).join(', ')}</strong>
                          </Alert>
                          <Button
                            variant="outlined"
                            startIcon={populationLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                            disabled={populationLoading || selectedPopulations.length === 0}
                            onClick={async () => {
                              const requestId = ++populationRequestId.current;
                              setPopulationLoading(true);
                              setPopulationFetched(false);
                              setPopulationError('');
                              // Never keep an earlier list while this one loads or after it fails.
                              setPopulationRecipients([]);
                              setError('');
                              try {
                                const results = await fetchPopulation(selectedPopulations);
                                if (requestId !== populationRequestId.current) return; // selection changed
                                setPopulationRecipients(results);
                                setPopulationFetched(true);
                              } catch (err: any) {
                                if (requestId !== populationRequestId.current) return;
                                setPopulationRecipients([]);
                                setPopulationError(populationFetchErrorMessage(err));
                              } finally {
                                if (requestId === populationRequestId.current) setPopulationLoading(false);
                              }
                            }}
                          >
                            {populationLoading ? 'Fetching…' : populationFetched ? 'Refresh Recipients' : 'Fetch Recipients'}
                          </Button>

                          {populationLoading && selectedPopulations.some(p => STUDENT_POPULATIONS.includes(p)) && (
                            <Typography variant="caption" color="text.secondary">
                              Student lists can take up to 3 minutes. Please keep this page open.
                            </Typography>
                          )}

                          {populationError && (
                            <Alert severity="error">{populationError}</Alert>
                          )}

                          {populationFetched && (
                            <Alert severity={populationRecipients.length > 0 ? 'success' : 'warning'}>
                              {populationRecipients.length > 0
                                ? `${populationRecipients.length} recipient${populationRecipients.length !== 1 ? 's' : ''} ready to be contacted`
                                : 'No recipients found for the selected population(s)'}
                            </Alert>
                          )}

                          {populationRecipients.length > 0 && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PeopleIcon />}
                              onClick={() => setShowRecipientsModal(true)}
                            >
                              View &amp; Edit Recipients ({populationRecipients.length})
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* ── Recipients modal ── */}
                    <Dialog
                      open={showRecipientsModal}
                      onClose={() => setShowRecipientsModal(false)}
                      fullWidth
                      maxWidth="sm"
                      PaperProps={{ sx: { maxHeight: '80vh' } }}
                    >
                      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Box>
                          Recipients
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({populationRecipients.length} total)
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setShowRecipientsModal(false)}>✕</IconButton>
                      </DialogTitle>
                      <DialogContent dividers sx={{ p: 0, overflowY: 'auto' }}>
                        {populationRecipients.length === 0 ? (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="text.secondary">No recipients remaining.</Typography>
                          </Box>
                        ) : (
                          populationRecipients.map((r, i) => (
                            <Box
                              key={r.email}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1.25,
                                borderBottom: i < populationRecipients.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {r.firstName} {r.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {r.email}{r.jobTitle ? ` · ${r.jobTitle}` : ''}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setPopulationRecipients(prev => prev.filter(x => x.email !== r.email))}
                                title="Remove recipient"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))
                        )}
                      </DialogContent>
                      <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {populationRecipients.length} recipient{populationRecipients.length !== 1 ? 's' : ''} remaining
                          </Typography>
                          {duplicateInfo.checked && duplicateInfo.emails.length > 0 && (
                            <Chip label={`${duplicateInfo.emails.length} duplicate email${duplicateInfo.emails.length !== 1 ? 's' : ''}`} color="warning" size="small" />
                          )}
                          {duplicateInfo.checked && duplicateInfo.emails.length === 0 && (
                            <Chip label="No duplicates" color="success" size="small" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {duplicateInfo.checked && duplicateInfo.emails.length > 0 && (
                            <Button
                              onClick={() => {
                                const seen = new Set<string>();
                                setPopulationRecipients(prev => prev.filter(r => {
                                  if (seen.has(r.email)) return false;
                                  seen.add(r.email);
                                  return true;
                                }));
                                setDuplicateInfo({ emails: [], checked: false });
                              }}
                              variant="outlined"
                              color="warning"
                              size="small"
                            >
                              Remove Duplicates
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              const emailCounts: Record<string, number> = {};
                              populationRecipients.forEach(r => {
                                emailCounts[r.email] = (emailCounts[r.email] || 0) + 1;
                              });
                              const dupes = Object.entries(emailCounts)
                                .filter(([, count]) => count > 1)
                                .map(([email]) => email);
                              setDuplicateInfo({ emails: dupes, checked: true });
                            }}
                            variant="outlined"
                            size="small"
                          >
                            Check for Duplicates
                          </Button>
                          <Button
                            onClick={() => {
                              setShowRecipientsModal(false);
                              setDuplicateInfo({ emails: [], checked: false });
                            }}
                            variant="contained"
                            size="small"
                          >
                            Done
                          </Button>
                        </Box>
                      </DialogActions>
                    </Dialog>

                    {recipientInputMethod === 'csv' && (
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Upload CSV File
                        </Typography>
                      <Paper
                        {...getRootProps()}
                        sx={{
                          p: 4,
                          border: '2px dashed',
                          borderColor: isDragActive ? 'primary.main' : 'grey.300',
                          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s',
                        }}
                      >
                        <input {...getInputProps()} />
                        <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        {file ? (
                          <Typography variant="body1">
                            Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="body1" gutterBottom>
                              Drag & drop a CSV file here, or click to select
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              CSV should contain: email, and any template variables
                            </Typography>
                          </>
                        )}
                      </Paper>
                    </Box>
                    )}

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: 'var(--text-secondary)' }}>
                          Sender Information
                        </Typography>
                        <Tooltip title="Open Address Book">
                          <Button
                            size="small"
                            startIcon={<ImportContactsIcon />}
                            onClick={() => setOpenAddressBook(true)}
                            sx={{ color: 'var(--accent)', textTransform: 'none' }}
                          >
                            Address Book
                          </Button>
                        </Tooltip>
                      </Box>
                      <Stack spacing={2}>
                        <TextField
                          label="From Name"
                          value={fromName}
                          onChange={(e) => setFromName(e.target.value)}
                          required
                          fullWidth
                          helperText="Display name recipients will see"
                        />
                        <TextField
                          label="From Email"
                          type="email"
                          value={fromEmail}
                          onChange={(e) => setFromEmail(e.target.value)}
                          required
                          fullWidth
                          helperText="Email address the message is sent from"
                        />
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
                      <Button onClick={() => setActiveStep(1)} disabled={loading}>
                        Back
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/ECM/Dashboard')}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || populationLoading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                      >
                        {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Campaign' : 'Create Campaign')}
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </StepContent>
            </Step>
          </Stepper>
        </Paper>

        {/* CSV Format Requirements */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            CSV Format Requirements
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            Your CSV file can use either of these two formats:
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Paper 
              elevation={2}
              sx={{ 
                flex: 1, 
                p: 2.5, 
                border: '2px solid',
                borderColor: 'primary.light',
                backgroundColor: 'primary.50'
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'var(--accent)' }}>
                Format 1: Standard
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                Use <strong>email</strong> column
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: '#1a1a1a' }}>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', m: 0, color: 'var(--success)', fontSize: '0.85rem' }}>
                  {`email,firstName,lastName
john@example.com,John,Doe
jane@example.com,Jane,Smith`}
                </Typography>
              </Paper>
            </Paper>

            <Paper 
              elevation={2}
              sx={{ 
                flex: 1, 
                p: 2.5, 
                border: '2px solid',
                borderColor: 'secondary.light',
                backgroundColor: 'secondary.50'
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'var(--accent)' }}>
                Format 2: Dual Email
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                Use <strong>email1</strong> column
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: '#1a1a1a' }}>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', m: 0, color: 'var(--success)', fontSize: '0.85rem' }}>
                  {`firstName,lastName,email1,email2
John,Doe,john@example.com,john@example.com
Jane,Smith,jane@example.com,jane@example.com`}
                </Typography>
              </Paper>
            </Paper>
          </Box>

          {/* Canvas Announcement CSV Format */}
          {selectedTypes.includes('canvas_announcement') && (
            <Paper 
              elevation={2}
              sx={{ 
                mt: 2,
                p: 2.5, 
                border: '2px solid',
                borderColor: 'success.main',
                backgroundColor: 'success.50'
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'var(--accent)' }}>
                Canvas Announcement Format (Required)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                For Canvas announcements, you <strong>must</strong> include <strong>course_id</strong> column
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: '#1a1a1a' }}>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', m: 0, color: 'var(--success)', fontSize: '0.85rem' }}>
                  {`email,firstName,lastName,course_id
john@example.com,John,Doe,12345
jane@example.com,Jane,Smith,12345
bob@example.com,Bob,Johnson,67890`}
                </Typography>
              </Paper>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> The <code>course_id</code> is the Canvas course ID where the announcement will be posted. 
                  Students in the same course will see the same announcement. You can find course IDs in your Canvas URL (e.g., /courses/12345).
                </Typography>
              </Alert>
            </Paper>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Additional columns are automatically included for email templates.
            </Typography>
          </Alert>
        </Paper>

        {/* ── Address Book Dialog ── */}
        <Dialog
          open={openAddressBook}
          onClose={() => { setOpenAddressBook(false); setNewFromName(''); setNewFromEmail(''); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImportContactsIcon sx={{ color: 'var(--accent)' }} />
            Address Book
          </DialogTitle>
          <DialogContent>
            {/* Saved pairs */}
            {fromAddresses.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No saved addresses yet. Add one below.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {fromAddresses.map((addr, index) => {
                  const isActive = fromEmail === addr.email && fromName === addr.name;
                  return (
                    <Paper
                      key={index}
                      elevation={isActive ? 3 : 1}
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        border: '1px solid',
                        borderColor: isActive ? 'var(--accent)' : 'divider',
                        backgroundColor: isActive ? 'rgba(0,51,102,0.06)' : 'var(--card-bg)',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" noWrap fontWeight={isActive ? 700 : 400}>
                          {addr.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {addr.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant={isActive ? 'contained' : 'outlined'}
                          onClick={() => {
                            setFromName(addr.name);
                            setFromEmail(addr.email);
                            setOpenAddressBook(false);
                          }}
                          sx={{ minWidth: 72 }}
                        >
                          {isActive ? '✓ Active' : 'Use'}
                        </Button>
                        {addr.email !== defaultAddress.email && (
                          <Tooltip title="Remove from address book">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const updated = fromAddresses.filter((_, i) => i !== index);
                                setFromAddresses(updated);
                                localStorage.setItem('ecm_from_addresses', JSON.stringify(updated));
                                if (fromEmail === addr.email) {
                                  setFromName(defaultAddress.name);
                                  setFromEmail(defaultAddress.email);
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Add new entry */}
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'var(--text-secondary)' }}>
              Add New Entry
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="From Name"
                value={newFromName}
                onChange={(e) => setNewFromName(e.target.value)}
                fullWidth
                size="small"
                helperText="Display name for the sender"
              />
              <TextField
                label="From Email"
                type="email"
                value={newFromEmail}
                onChange={(e) => setNewFromEmail(e.target.value)}
                fullWidth
                size="small"
                helperText="Email address of the sender"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!newFromName || !newFromEmail}
                onClick={() => {
                  if (newFromName && newFromEmail) {
                    const isDuplicate = fromAddresses.some(
                      a => a.email.toLowerCase() === newFromEmail.toLowerCase()
                    );
                    if (!isDuplicate) {
                      const updated = [...fromAddresses, { name: newFromName, email: newFromEmail }];
                      setFromAddresses(updated);
                      localStorage.setItem('ecm_from_addresses', JSON.stringify(updated));
                    }
                    setFromName(newFromName);
                    setFromEmail(newFromEmail);
                    setNewFromName('');
                    setNewFromEmail('');
                    setOpenAddressBook(false);
                  }
                }}
              >
                Save &amp; Use
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setOpenAddressBook(false); setNewFromName(''); setNewFromEmail(''); }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
        )}
      </Box>
    </Box>
  );
}
